import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FiSettings, FiX, FiSave, FiDroplet, FiWind, FiThermometer, FiActivity } from "react-icons/fi";

const ControllerSettingsForm = ({ initialConfig = {}, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (initialConfig) {
      setValue("irrigation.on_cycle_hours", initialConfig.irrigation?.on_cycle_hours || "");
      setValue("irrigation.off_cycle_liters", initialConfig.irrigation?.off_cycle_liters || "");
      setValue("irrigation.soil_moisture_on", initialConfig.irrigation?.soil_moisture_on || "");
      setValue("irrigation.soil_moisture_off", initialConfig.irrigation?.soil_moisture_off || "");
      setValue("irrigation.moisture_min_alert", initialConfig.irrigation?.moisture_min_alert || "");
      setValue("irrigation.moisture_max_alert", initialConfig.irrigation?.moisture_max_alert || "");


      setValue("ventilation.co2_threshold", initialConfig.ventilation?.co2_threshold || "");
      setValue("ventilation.co2_min_alert", initialConfig.ventilation?.co2_min_alert || "");
      setValue("ventilation.co2_max_alert", initialConfig.ventilation?.co2_max_alert || "");

      setValue("ac.based_on", initialConfig.ac?.based_on || "soil_temp");
      setValue("ac.target_temp", initialConfig.ac?.target_temp || "");
      setValue("ac.temp_alert", initialConfig.ac?.temp_min_alert || "");
      setValue("ac.temp_alert", initialConfig.ac?.temp_max_alert || "");

      setValue("humidifier.humidity_on_below", initialConfig.humidifier?.humidity_on_below || "");
      setValue("humidifier.humidity_off_above", initialConfig.humidifier?.humidity_off_above || "");
      setValue("humidifier.humidity_min_alert", initialConfig.humidifier?.humidity_min_alert || "");
      setValue("humidifier.humidity_max_alert", initialConfig.humidifier?.humidity_max_alert || "");
    }
  }, [initialConfig, setValue]);

  const submitForm = (data) => {
    const formatted = {
      irrigation: {
        on_cycle_hours: +data.irrigation.on_cycle_hours,
        off_cycle_liters: +data.irrigation.off_cycle_liters,
        soil_moisture_on: +data.irrigation.soil_moisture_on,
        soil_moisture_off: +data.irrigation.soil_moisture_off,
        moisture_min_alert:+data.irrigation.moisture_min_alert || 0,
        moisture_max_alert:+data.irrigation.moisture_max_alert || 0,
      },
      ventilation: {
        co2_threshold: +data.ventilation.co2_threshold,
        co2_min_alert:+data.ventilation.co2_min_alert || 0,
        co2_max_alert:+data.ventilation.co2_max_alert || 0,
      },
      ac: {
        based_on: data.ac.based_on,
        target_temp: +data.ac.target_temp,
        temp_min_alert:+data.ac.temp_min_alert || 0,
        temp_max_alert:+data.ac.temp_max_alert || 0,
      },
      humidifier: {
        humidity_on_below: +data.humidifier.humidity_on_below,
        humidity_off_above: +data.humidifier.humidity_off_above,
        humidity_min_alert:+data.humidifier.humidity_min_alert || 0,
        humidity_max_alert:+data.humidifier.humidity_max_alert || 0,        
      },
    };
    onSubmit(formatted);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
      {/* Irrigation Section */}
      <section className="p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="flex items-center text-lg font-semibold text-blue-700 mb-2">
          <FiDroplet className="mr-2" /> Irrigation Settings
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">On Cycle (hours)</label>
            <input type="number" {...register("irrigation.on_cycle_hours", { required: true })} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Off Cycle (liters)</label>
            <input type="number" {...register("irrigation.off_cycle_liters", { required: true })} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Soil Moisture On (%)</label>
            <input type="number" {...register("irrigation.soil_moisture_on", { required: true })} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Soil Moisture Off (%)</label>
            <input type="number" {...register("irrigation.soil_moisture_off", { required: true })} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Moisture Min Alert (%)</label>
            <input type="number" {...register("irrigation.moisture_min_alert")} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Moisture Max Alert (%)</label>
            <input type="number" {...register("irrigation.moisture_max_alert")} className="input px-3 py-2 border rounded-lg" />
          </div>
        </div>
      </section>

      {/* Ventilation Section */}
      <section className="p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="flex items-center text-lg font-semibold text-green-700 mb-2">
          <FiWind className="mr-2" /> Ventilation Settings
        </h2>
        <label className="block text-sm font-medium">CO₂ Threshold (ppm)</label>
        <input type="number" {...register("ventilation.co2_threshold", { required: true })} className="input px-3 py-2 border rounded-lg" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium">CO₂ Min Alert (ppm)</label>
            <input type="number" {...register("ventilation.co2_min_alert")} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">CO₂ Max Alert (ppm)</label>
            <input type="number" {...register("ventilation.co2_max_alert")} className="input px-3 py-2 border rounded-lg" />
          </div>
        </div>
      </section>

      {/* AC Section */}
      <section className="p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="flex items-center text-lg font-semibold text-purple-700 mb-2">
          <FiThermometer className="mr-2" /> AC Settings
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Based On</label>
            <select {...register("ac.based_on", { required: true })} className="input">
              <option value="soil_temp">Soil Temperature</option>
              <option value="room_temp">Room Temperature</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Target Temperature (°C)</label>
            <input type="number" {...register("ac.target_temp", { required: true })} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Temperature Alert (°C)</label>
            <input type="number" {...register("ac.temp_min_alert")} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Temperature Alert (°C)</label>
            <input type="number" {...register("ac.temp_max_alert")} className="input px-3 py-2 border rounded-lg" />
          </div>

        </div>
      </section>

      {/* Humidifier Section */}
      <section className="p-4 bg-white border rounded-lg shadow-sm">
        <h2 className="flex items-center text-lg font-semibold text-indigo-700 mb-2">
          <FiActivity className="mr-2" /> Humidifier Settings
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Turn On Below (%)</label>
            <input type="number" {...register("humidifier.humidity_on_below", { required: true })} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Turn Off Above (%)</label>
            <input type="number" {...register("humidifier.humidity_off_above", { required: true })} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Humidity Min Alert (%)</label>
            <input type="number" {...register("humidifier.humidity_min_alert")} className="input px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium">Humidity Max Alert (%)</label>
            <input type="number" {...register("humidifier.humidity_max_alert")} className="input px-3 py-2 border rounded-lg" />
          </div>
        </div>
      </section>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary">
          <FiSave className="inline-block mr-1" /> Save Settings
        </button>
      </div>
    </form>
  );
};

export default ControllerSettingsForm;
